import React, { useEffect, useMemo } from 'react';
import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import { Breakpoint, Button, Constrain, Stack, Text } from '@moda/om';
import { compact, formatVertical } from '@moda/portal-stanchions';
import { TrunkshowIndexPageQuery, TrunkshowIndexPageQueryVariables } from '../../generated/types';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { hrefFor } from '../../routers';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useUrlParams } from '../../hooks/useUrlParams';
import { Loading } from '../../components/Loading';
import { useCountry } from '../../components/CountrySelector';
import { ModuleSection } from '../../components/ModuleSection';
import { PUMO_MODULE_FRAGMENT } from '../../components/PumoModule';
import { usePageType } from '../../hooks/usePageType';
import { TrunkshowIndexPageCollections } from './TrunkshowIndexPageCollections';
import { TrunkshowIndexPagePieces } from './TrunkshowIndexPagePieces';

import './TrunkshowIndexPage.scss';

export const TRUNKSHOW_INDEX_PAGE_QUERY: TypedDocumentNode<
  TrunkshowIndexPageQuery,
  TrunkshowIndexPageQueryVariables
> = gql`
  query TrunkshowIndexPageQuery($slug: String!, $countryCode: String, $versionId: Int) {
    displayPage(slug: $slug, version_id: $versionId) {
      targetBuckets {
        name
        modules {
          ...PumoModuleFragment
        }
      }
    }
  }
  ${PUMO_MODULE_FRAGMENT}
`;

export const TrunkshowIndexPage: React.FC = () => {
  const {
    params: { category, versionId }
  } = usePLPParams();
  const { country } = useCountry();
  const { params } = useUrlParams();

  const mode = (params.mode as 'designers' | undefined) || 'pieces';

  const { data, loading, error } = useQuery(TRUNKSHOW_INDEX_PAGE_QUERY, {
    variables: {
      // TODO: remove women from this (requires changes in mojo/search-api)
      slug: 'women/trunkshows',
      countryCode: country.alpha2Code,
      versionId
    }
  });

  const pageType = usePageType();

  useEffect(() => {
    if (pageType === 'TLP') {
      localStorage.setItem('TLPVisited', 'true');
    }
  }, [pageType]);

  const headerModules = useMemo(
    () => compact(data?.displayPage?.targetBuckets).find(({ name }) => name === 'header')?.modules,
    [data]
  );

  const verticalText = formatVertical('women');
  const title = useMemo(() => {
    const genderTitle = `Designer Runway Collections & Fashion Shows`;
    const categoryTitle = `Designer ${category} Collections from the Runway`;
    return `${verticalText} ${category ? categoryTitle : genderTitle}`;
  }, [category, verticalText]);

  const description = `Discover & preorder the latest from ${verticalText} fashion collections, runway shows, lookbooks & more through Trunkshow on Moda Operandi.`;

  if (error) throwQueryError(error);

  return (
    <>
      {loading && <Loading />}
      <div className="TrunkshowIndexPage">
        <Constrain>
          <Stack space={4}>
            {headerModules && <ModuleSection modules={headerModules} />}

            <h1 className="TrunkshowIndexPage__header">Trunkshows</h1>

            <Stack space={1} className="TrunkshowIndexPage__description" justifyContent="center">
              <Breakpoint gt="sm">
                <Text treatment="h6">
                  Pre-order directly from the runway, ahead of the crowd. Each Trunkshow is live for
                  a limited amount of time, and <br /> many styles featured will never make it into
                  stores—meaning you'll be one of a select few to secure a particular look.
                </Text>
              </Breakpoint>

              <Breakpoint lt="sm">
                <Text treatment="h6">
                  Pre-order directly from the runway, <br /> ahead of the crowd. Each Trunkshow is
                  <br />
                  live for a limited amount of time, and <br /> many styles featured will never make
                  it <br />
                  into stores—meaning you'll be one of a <br /> select few to secure a particular
                  look.
                </Text>
              </Breakpoint>
              <Text treatment="h6" className="TrunkshowIndexPage__bold">
                See It. Love it. Own it.
              </Text>
            </Stack>

            {!loading && (
              <Stack className="TrunkshowIndexPage__selectors" space={4}>
                <Text
                  align-self="center"
                  treatment="body2"
                  className="TrunkshowIndexPage__selectors-header"
                >
                  SHOP COLLECTION BY
                </Text>
                <Stack direction="horizontal" space={4}>
                  <Button
                    chip
                    secondary={mode !== 'designers'}
                    to={hrefFor.TrunkshowIndexPage({ mode: 'designers' })}
                    className="TrunkshowIndexPage__change-mode-button"
                  >
                    DESIGNERS
                  </Button>
                  <Button
                    chip
                    secondary={mode !== 'pieces'}
                    to={hrefFor.TrunkshowIndexPage()}
                    className="TrunkshowIndexPage__change-mode-button"
                  >
                    PIECES
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Constrain>

        {mode === 'designers' ? (
          <TrunkshowIndexPageCollections
            title={title}
            description={description}
            loading={loading}
          />
        ) : (
          <TrunkshowIndexPagePieces title={title} description={description} loading={loading} />
        )}
      </div>
    </>
  );
};
